import { useEffect, useRef, useState } from 'react';
import { fetchAndActivate, getRemoteConfig, getValue, isSupported } from 'firebase/remote-config';

import { app } from '../firebaseConfig';

const variantStyles = {
  brand: {
    backgroundColor: '#7a41ca',
    textColor: 'var(--bs-light)',
  },
  light: {
    backgroundColor: 'var(--bs-gray-200)',
    textColor: '#4b00b3',
  },
  dark: {
    backgroundColor: 'var(--bs-gray-600)',
    textColor: 'var(--bs-light)',
  },
  danger: {
    backgroundColor: 'var(--bs-danger)',
    textColor: 'var(--bs-light)',
  },
};

const MessageBanner = () => {
  const [banner, setBanner] = useState(null);
  const contentRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const loadBanner = async () => {
      try {
        const supported = await isSupported();
        if (!supported || cancelled) {
          return;
        }

        const remoteConfig = getRemoteConfig(app);
        remoteConfig.settings = {
          minimumFetchIntervalMillis:
            process.env.REACT_APP_VERCEL_ENV === 'development' ? 0 : 60 * 60 * 1000,
        };
        remoteConfig.defaultConfig = {
          message_banner_enabled: false,
          message_banner_text: '',
          message_banner_variant: 'brand',
        };

        await fetchAndActivate(remoteConfig);
        if (cancelled) {
          return;
        }

        const enabled = getValue(remoteConfig, 'message_banner_enabled').asBoolean();
        if (!enabled) {
          setBanner(null);
          return;
        }

        const text = getValue(remoteConfig, 'message_banner_text').asString();
        const variant = (
          getValue(remoteConfig, 'message_banner_variant').asString() || 'brand'
        ).toLowerCase();

        setBanner({
          text,
          variant,
        });
      } catch (error) {
        console.error('Unable to load message banner remote config', error);
      }
    };

    loadBanner();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!contentRef.current || !banner) {
      return;
    }

    contentRef.current.querySelectorAll('a[target="_blank"]').forEach((anchor) => {
      const existingRel = anchor.getAttribute('rel') || '';
      const relParts = new Set(existingRel.split(' ').filter(Boolean));
      relParts.add('noopener');
      relParts.add('noreferrer');
      anchor.setAttribute('rel', Array.from(relParts).join(' '));
    });
  }, [banner]);

  if (!banner || !banner.text) {
    return null;
  }
  const theme = variantStyles[banner.variant] || variantStyles.brand;
  const containerStyle = {
    backgroundColor: theme.backgroundColor,
    color: theme.textColor,
    textAlign: 'center',
    padding: '10px 10px',
    lineHeight: 1.3,
    fontSize: '0.9rem',
    fontWeight: 500,
    letterSpacing: '0.02rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    position: 'relative',
  };

  return (
    <div style={containerStyle}>
      <div
        ref={contentRef}
        className='message-banner__content'
        style={{ margin: 0, padding: 0, '--message-banner-text-color': theme.textColor }}
        dangerouslySetInnerHTML={{ __html: banner.text }}
      />
    </div>
  );
};

export default MessageBanner;
